import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Plus, Printer, Mail, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { invoicesAPI, customersAPI, productsAPI, companyAPI } from '../../services/api';
import { printNode } from '../../utils/print';
import InvoiceDocument from '../common/InvoiceDocument';
import EmailModal from '../common/EmailModal';
import { useCompany } from '../common/useCompany';
import CustomerSelect from '../common/CustomerSelect';
import BillToEditor, { EMPTY_BILL_TO, billToFromCustomer, billToPatch, billToChanged } from './BillToEditor';
import { generateInvoicePDF } from '../../utils/generateDocumentPDF';
import '../../styles/invoice-editor.css';
import { getDisplayCurrency, CURRENCY_REGISTRY, convertAmount, rateLabel } from '../../utils/currency';

const fmt = (v) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);
const TERMS = ['Due on Receipt', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Net 90'];
const MESSAGES = [
  'Thank you for your business!',
  'Please pay within the terms specified.',
  'Payment is due upon receipt.',
  'We appreciate your prompt payment.',
  '',
];

function makeEmptyRow(n) {
  return { id: n, product_id: '', description: '', quantity: 1, rate: 0, amount: 0, tax_rate: 0 };
}

export default function InvoiceEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  // Invoice state
  const [form, setForm] = useState({
    customer_id: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
    currency: getDisplayCurrency(),
    exchange_rate: 1,
    po_number: '',
    terms: 'Net 30',
    customer_message: 'Thank you for your business!',
    memo: '',
  });

  const [rows, setRows] = useState([makeEmptyRow(1), makeEmptyRow(2), makeEmptyRow(3), makeEmptyRow(4), makeEmptyRow(5)]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [currencies, setCurrencies] = useState(CURRENCY_REGISTRY);
  const [rates, setRates] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // Editable "Bill To" block. Starts from the selected customer and is written
  // back to the customer record on save.
  const [billTo, setBillTo] = useState({ ...EMPTY_BILL_TO });
  const [savedBillTo, setSavedBillTo] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [tax, setTax] = useState(0);
  const [shipping, setShipping] = useState(0);
  const [paymentsApplied, setPaymentsApplied] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Item autocomplete state
  const [itemDropdown, setItemDropdown] = useState(null); // { rowId, x, y }
  const [itemSearch, setItemSearch] = useState('');
  const itemInputRef = useRef(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', sku: '', description: '', sales_price: '', purchase_cost: '', tax_rate: '', product_type: 'NonInventory', category_id: '' });
  const [categories, setCategories] = useState([]);
  const [savingProduct, setSavingProduct] = useState(false);
  const company = useCompany();
  const [showPrint, setShowPrint] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const printDocRef = useRef(null);

  // Load data
  useEffect(() => {
    customersAPI.list({}).then(setCustomers).catch(() => {});
    productsAPI.list({}).then(setProducts).catch(() => {});
    productsAPI.categories().then(setCategories).catch(() => {});
    companyAPI.currencies().then((cs) => { if (Array.isArray(cs) && cs.length) setCurrencies(cs); }).catch(() => {});
    companyAPI.exchangeRates().then((rs) => setRates(Array.isArray(rs) ? rs : [])).catch(() => {});
  }, []);

  // Load existing invoice for edit
  useEffect(() => {
    if (isEdit) {
      invoicesAPI.get(id).then(inv => {
        setForm({
          customer_id: inv.customer_id || '',
          invoice_number: inv.invoice_number || '',
          invoice_date: inv.invoice_date || '',
          due_date: inv.due_date || '',
          currency: inv.currency || 'USD',
          exchange_rate: inv.exchange_rate || 1,
          po_number: inv.po_number || '',
          terms: inv.payment_terms || 'Net 30',
          customer_message: inv.memo || '',
          memo: inv.description || '',
        });
        setDiscount(parseFloat(inv.discount_amount) || 0);
        setShipping(parseFloat(inv.shipping_amount) || 0);
        setPaymentsApplied(parseFloat(inv.amount_paid) || 0);
        if (inv.items?.length) {
          setRows(inv.items.map((item, i) => ({
            id: i + 1,
            product_id: item.product_id || '',
            description: item.description || item.product_name || '',
            quantity: parseFloat(item.quantity) || 1,
            rate: parseFloat(item.unit_price) || 0,
            amount: parseFloat(item.amount) || 0,
            tax_rate: parseFloat(item.tax_rate) || 0,
          })));
        }
        // GET /invoices/:id only joins the customer name, so pull the full
        // record to seed the editable Bill To block.
        if (inv.customer_id) {
          setSelectedCustomer(c => c || { id: inv.customer_id, name: inv.customer_name });
          customersAPI.get(inv.customer_id).then(c => {
            setCustomers(prev => (prev.some(x => x.id === c.id) ? prev : [...prev, c]));
            setSelectedCustomer(c);
            const bt = billToFromCustomer(c);
            setBillTo(bt);
            setSavedBillTo(bt);
          }).catch(() => {
            const bt = billToFromCustomer({ name: inv.customer_name });
            setBillTo(bt);
            setSavedBillTo(bt);
          });
        }
      }).catch(e => toast.error(e.message));
    }
  }, [id, isEdit]);

  // Auto-set customer info
  const selectCustomer = useCallback((cust) => {
    // CustomerSelect passes the full record, including one it just created
    // inline (which is not in the `customers` list yet).
    const custId = cust && typeof cust === 'object' ? cust.id : cust;
    const found = (cust && typeof cust === 'object' && cust.name ? cust : null)
      || customers.find(c => c.id === parseInt(custId));
    if (cust && typeof cust === 'object' && cust.id) {
      setCustomers(prev => (prev.some(c => c.id === cust.id) ? prev : [...prev, cust]));
    }
    setSelectedCustomer(found || null);
    setForm(f => ({ ...f, customer_id: custId }));
    const bt = billToFromCustomer(found);
    setBillTo(bt);
    setSavedBillTo(bt);
    if (found) {
      setForm(f => ({
        ...f,
        customer_id: custId,
        terms: found.payment_terms || f.terms,
        currency: found.currency || f.currency,
      }));
    }
  }, [customers]);

  // Write the edited Bill To block back to the customer record.
  const persistBillTo = useCallback(async (values) => {
    const customerId = form.customer_id;
    if (!customerId) throw new Error('Select a customer first');
    if (!String(values.name || '').trim()) throw new Error('Bill To name is required');
    const res = await customersAPI.update(customerId, values);
    // The API appends a `message` field; keep it out of the customer record.
    const { message, ...updated } = res;
    setCustomers(prev => prev.map(c => (c.id === parseInt(customerId) ? { ...c, ...updated } : c)));
    setSelectedCustomer(prev => (prev ? { ...prev, ...updated } : prev));
    setSavedBillTo(billToFromCustomer({ ...(selectedCustomer || {}), ...updated }));
    toast.success('Customer information updated');
    return updated;
  }, [form.customer_id, selectedCustomer]);

  // Calculations
  const subtotal = useMemo(() => rows.reduce((s, r) => s + (r.amount || 0), 0), [rows]);
  const taxAmount = useMemo(() => {
    const lineTax = rows.reduce((s, r) => s + ((r.amount || 0) * (r.tax_rate || 0) / 100), 0);
    return lineTax + tax;
  }, [rows, tax]);
  const total = useMemo(() => subtotal - discount + taxAmount + shipping, [subtotal, discount, taxAmount, shipping]);
  const balanceDue = useMemo(() => total - paymentsApplied, [total, paymentsApplied]);

  // Currency & exchange rate
  const baseCurrency = company?.base_currency || getDisplayCurrency();
  const docCurrency = (form.currency && /^[A-Z]{3}$/.test(String(form.currency))) ? form.currency : (baseCurrency || 'USD');
  const fxRate = parseFloat(form.exchange_rate);
  const effectiveRate = isNaN(fxRate) || fxRate <= 0 ? 1 : fxRate;

  // Auto-fill the exchange rate when the invoice currency is not the company base.
  useEffect(() => {
    if (docCurrency === baseCurrency) {
      if (form.exchange_rate && parseFloat(form.exchange_rate) !== 1) {
        setForm(f => ({ ...f, exchange_rate: 1 }));
      }
      return;
    }
    const direct = rates.find(r => r.from_currency === docCurrency && r.to_currency === baseCurrency);
    const reverse = rates.find(r => r.from_currency === baseCurrency && r.to_currency === docCurrency);
    const auto = direct ? parseFloat(direct.rate) : (reverse && parseFloat(reverse.rate) > 0) ? 1 / parseFloat(reverse.rate) : null;
    const cur = parseFloat(form.exchange_rate);
    // exchange_rate starts at 1 (placeholder) — treat as unset so auto-fill applies.
    if (auto != null && auto > 0 && (!form.exchange_rate || cur === 1)) {
      setForm(f => ({ ...f, exchange_rate: auto }));
    }
  }, [docCurrency, baseCurrency, rates]);

  const currencySymbol = useMemo(() => {
    const c = currencies.find(x => x.code === form.currency);
    return c ? (c.symbol || c.code) : form.currency;
  }, [currencies, form.currency]);

  // Print/PDF use the live Bill To values, so an edit shows up in the preview
  // even before the customer record is written.
  const billToDoc = useMemo(() => ({
    customer_name: billTo.name || selectedCustomer?.name,
    customer_email: billTo.email || selectedCustomer?.email,
    customer_phone: billTo.phone || selectedCustomer?.phone,
    customer_address: [
      billTo.address_line1,
      billTo.address_line2,
      [billTo.city, billTo.state, billTo.postal_code].filter(Boolean).join(', '),
      billTo.country,
    ].filter(Boolean).join(', ') || null,
  }), [billTo, selectedCustomer]);

  // Row updates
  const updateRow = useCallback((rowId, field, value) => {
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      const updated = { ...r, [field]: value };
      if (field === 'quantity' || field === 'rate') {
        updated.amount = (parseFloat(updated.quantity) || 0) * (parseFloat(updated.rate) || 0);
      }
      return updated;
    }));
  }, []);

  const addRow = useCallback(() => {
    setRows(prev => [...prev, makeEmptyRow(Date.now())]);
  }, []);

  const removeRow = useCallback((rowId) => {
    setRows(prev => {
      if (prev.length <= 1) return prev;
      return prev.filter(r => r.id !== rowId);
    });
  }, []);

  // Item selection
  const selectProduct = useCallback((rowId, product) => {
    updateRow(rowId, 'product_id', product.id);
    updateRow(rowId, 'description', product.description || product.name);
    updateRow(rowId, 'rate', parseFloat(product.sales_price) || 0);
    updateRow(rowId, 'tax_rate', parseFloat(product.tax_rate) || 0);
    setItemDropdown(null);
    setItemSearch('');
  }, [updateRow]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e, rowId, field, rowIdx, fieldIdx) => {
    const fields = ['description', 'quantity', 'rate'];
    if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        // Move left
        if (fieldIdx > 0) {
          const prevField = fields[fieldIdx - 1];
          document.querySelector(`[data-row="${rowId}"][data-field="${prevField}"]`)?.focus();
        } else if (rowIdx > 0) {
          const prevRow = rows[rowIdx - 1];
          const lastField = fields[fields.length - 1];
          document.querySelector(`[data-row="${prevRow.id}"][data-field="${lastField}"]`)?.focus();
        }
      } else {
        // Move right
        if (fieldIdx < fields.length - 1) {
          const nextField = fields[fieldIdx + 1];
          document.querySelector(`[data-row="${rowId}"][data-field="${nextField}"]`)?.focus();
        } else if (rowIdx < rows.length - 1) {
          const nextRow = rows[rowIdx + 1];
          document.querySelector(`[data-row="${nextRow.id}"][data-field="description"]`)?.focus();
        } else {
          addRow();
          setTimeout(() => {
            const newRow = rows[rows.length - 1];
            if (newRow) document.querySelector(`[data-row="${newRow.id}"][data-field="description"]`)?.focus();
          }, 50);
        }
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (rowIdx < rows.length - 1) {
        const nextRow = rows[rowIdx + 1];
        document.querySelector(`[data-row="${nextRow.id}"][data-field="description"]`)?.focus();
      } else {
        addRow();
      }
    } else if (e.key === 'Escape') {
      setItemDropdown(null);
    } else if (e.key === 'Delete' && e.shiftKey) {
      removeRow(rowId);
    }
  }, [rows, addRow, removeRow]);

  // Save (draft invoices persist until the user deletes them, so a customer
  // and line items are optional when saving)
  const handleSave = async (andNew = false) => {
    if (!form.invoice_date) { toast.error('Please enter invoice date'); return; }
    const validRows = rows.filter(r => r.description || r.rate > 0);
    const dueDate = form.due_date || form.invoice_date;

    setLoading(true);
    // Bill To edits belong on the customer record, not just this invoice. Push
    // them first so the invoice saves against up-to-date customer data; a
    // failure here (e.g. missing edit permission) must not block the invoice.
    if (form.customer_id && billToChanged(savedBillTo, billTo)) {
      try {
        await persistBillTo(billToPatch(billTo));
      } catch (e) {
        toast.error(`Invoice saved, but the customer details were not updated: ${e.message}`);
      }
    }
    try {
      const data = {
        customer_id: form.customer_id ? parseInt(form.customer_id) : null,
        invoice_date: form.invoice_date,
        due_date: dueDate,
        currency: form.currency,
        exchange_rate: parseFloat(form.exchange_rate) || 1,
        payment_terms: form.terms,
        description: form.memo,
        memo: form.customer_message,
        subtotal,
        discount_amount: discount,
        tax_amount: taxAmount,
        shipping_amount: shipping,
        total,
        items: validRows.map((r, i) => ({
          product_id: r.product_id || null,
          description: r.description,
          quantity: r.quantity,
          unit_price: r.rate,
          tax_rate: r.tax_rate || 0,
          amount: r.amount,
        })),
      };
      if (isEdit) {
        await invoicesAPI.update(id, data);
        toast.success('Invoice updated');
        if (andNew) navigate('/invoices/new');
        else navigate('/invoices');
      } else {
        const res = await invoicesAPI.create(data);
        toast.success('Invoice created');
        if (andNew) {
          setForm({
            customer_id: '', invoice_number: '', invoice_date: new Date().toISOString().split('T')[0],
            due_date: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
            currency: getDisplayCurrency(), exchange_rate: 1, po_number: '', terms: 'Net 30',
            customer_message: 'Thank you for your business!', memo: '',
          });
          setRows([makeEmptyRow(1), makeEmptyRow(2), makeEmptyRow(3), makeEmptyRow(4), makeEmptyRow(5)]);
          setSelectedCustomer(null);
          setBillTo({ ...EMPTY_BILL_TO });
          setSavedBillTo(null);
          setDiscount(0); setTax(0); setShipping(0); setPaymentsApplied(0);
        } else {
          navigate(`/invoices/${res.id}`);
        }
      }
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  const handleClear = () => {
    setForm({
      customer_id: '', invoice_number: '', invoice_date: new Date().toISOString().split('T')[0],
      due_date: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
      currency: getDisplayCurrency(), exchange_rate: 1, po_number: '', terms: 'Net 30',
      customer_message: 'Thank you for your business!', memo: '',
    });
    setRows([makeEmptyRow(1), makeEmptyRow(2), makeEmptyRow(3), makeEmptyRow(4), makeEmptyRow(5)]);
    setSelectedCustomer(null);
    setBillTo({ ...EMPTY_BILL_TO });
    setSavedBillTo(null);
    setDiscount(0); setTax(0); setShipping(0); setPaymentsApplied(0);
  };

  // Filtered products for item dropdown
  const filteredProducts = useMemo(() => {
    if (!itemSearch) return products.slice(0, 20);
    const q = itemSearch.toLowerCase();
    return products.filter(p =>
      p.name?.toLowerCase().includes(q) ||
      p.sku?.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q)
    ).slice(0, 20);
  }, [products, itemSearch]);

  return (
    <div className="inv-editor">
      {/* ===== TOOLBAR ===== */}
      <div className="inv-toolbar">
        <div className="inv-toolbar-group">
          <label>Customer:Job</label>
          <CustomerSelect
            value={form.customer_id}
            onChange={(c) => selectCustomer(c)}
            type="customer"
          />
        </div>
        <div className="inv-toolbar-sep" />
        <div className="inv-toolbar-group">
          <label>Account</label>
          <select className="inv-toolbar-select" style={{ minWidth: 160 }}>
            <option>Accounts Receivable</option>
          </select>
        </div>
        <div className="inv-toolbar-sep" />
        <div className="inv-toolbar-group">
          <label>Template</label>
          <select className="inv-toolbar-select" style={{ minWidth: 140 }}>
            <option>New Invoice Template</option>
          </select>
        </div>
        <div className="inv-toolbar-right">
          {isEdit && (
            <>
              <button type="button" className="inv-btn inv-btn-secondary" onClick={() => setShowPrint(true)} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Printer size={13} /> Print
              </button>
              <button type="button" className="inv-btn inv-btn-secondary" onClick={() => setShowEmail(true)} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Mail size={13} /> Email
              </button>
            </>
          )}
          <span className="inv-key-hint">TAB: next field &nbsp; ENTER: next row &nbsp; SHIFT+DEL: remove row</span>
        </div>
      </div>

      {/* ===== INVOICE BODY ===== */}
      <div className="inv-body">
        {/* Header */}
        <div className="inv-header">
          <div className="inv-header-left">
            <div className="inv-title">Invoice</div>
            <BillToEditor
              billTo={billTo}
              onChange={setBillTo}
              customerId={form.customer_id || null}
              customerName={selectedCustomer?.name}
              onSaveToCustomer={persistBillTo}
            />
          </div>
          <div className="inv-header-right">
            <div className="inv-field-row">
              <span className="inv-field-label">Date</span>
              <input className="inv-field-input" type="date" value={form.invoice_date} onChange={e => setForm(f => ({ ...f, invoice_date: e.target.value }))} />
            </div>
            <div className="inv-field-row">
              <span className="inv-field-label">Invoice #</span>
              <input className="inv-field-input" value={form.invoice_number} onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))} placeholder="Auto-generated" />
            </div>
            <div className="inv-field-row">
              <span className="inv-field-label">P.O. No.</span>
              <input className="inv-field-input" value={form.po_number} onChange={e => setForm(f => ({ ...f, po_number: e.target.value }))} />
            </div>
            <div className="inv-field-row">
              <span className="inv-field-label">Terms</span>
              <select className="inv-field-input" value={form.terms} onChange={e => setForm(f => ({ ...f, terms: e.target.value }))}>
                {TERMS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="inv-field-row">
              <span className="inv-field-label">Due Date</span>
              <input className="inv-field-input" type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
            </div>
          </div>
        </div>

        {/* ===== LINE ITEMS GRID ===== */}
        <div className="inv-grid-wrap" style={{ position: 'relative' }}>
          <table className="inv-grid">
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th className="col-item">Item</th>
                <th className="col-desc">Description</th>
                <th className="col-qty text-right">Qty</th>
                <th className="col-rate text-right">Rate</th>
                <th className="col-amount text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => (
                <tr key={row.id} className={selectedRow === row.id ? 'selected' : ''} onClick={() => setSelectedRow(row.id)}>
                  <td className="inv-row-num">{rowIdx + 1}</td>
                  <td>
                    <div className="inv-cell" style={{ position: 'relative' }}>
                      <input
                        value={(() => {
                          const p = products.find(pr => pr.id === parseInt(row.product_id));
                          return p ? p.name : '';
                        })()}
                        placeholder="Type or select..."
                        onFocus={(e) => {
                          setSelectedRow(row.id);
                          const rect = e.target.getBoundingClientRect();
                          const wrapRect = e.target.closest('.inv-grid-wrap').getBoundingClientRect();
                          setItemDropdown({ rowId: row.id, x: rect.left - wrapRect.left, y: rect.bottom - wrapRect.top });
                          setItemSearch('');
                        }}
                        onChange={(e) => {
                          setItemSearch(e.target.value);
                          if (!itemDropdown) {
                            const rect = e.target.getBoundingClientRect();
                            const wrapRect = e.target.closest('.inv-grid-wrap').getBoundingClientRect();
                            setItemDropdown({ rowId: row.id, x: rect.left - wrapRect.left, y: rect.bottom - wrapRect.top });
                          }
                        }}
                        onKeyDown={(e) => handleKeyDown(e, row.id, 'item', rowIdx, 0)}
                        data-row={row.id}
                        data-field="item"
                      />
                    </div>
                  </td>
                  <td>
                    <div className="inv-cell">
                      <input
                        value={row.description}
                        onChange={e => updateRow(row.id, 'description', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, row.id, 'description', rowIdx, 0)}
                        data-row={row.id}
                        data-field="description"
                      />
                    </div>
                  </td>
                  <td>
                    <div className="inv-cell">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="text-right"
                        value={row.quantity}
                        onChange={e => updateRow(row.id, 'quantity', parseFloat(e.target.value) || 0)}
                        onKeyDown={(e) => handleKeyDown(e, row.id, 'quantity', rowIdx, 1)}
                        data-row={row.id}
                        data-field="quantity"
                      />
                    </div>
                  </td>
                  <td>
                    <div className="inv-cell">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="text-right"
                        value={row.rate}
                        onChange={e => updateRow(row.id, 'rate', parseFloat(e.target.value) || 0)}
                        onKeyDown={(e) => handleKeyDown(e, row.id, 'rate', rowIdx, 2)}
                        data-row={row.id}
                        data-field="rate"
                      />
                    </div>
                  </td>
                  <td>
                    <div className="inv-cell">
                      <div className="cell-amount">{currencySymbol} {fmt(row.amount)}</div>
                    </div>
                  </td>
                </tr>
              ))}
              {/* Add row button */}
              <tr>
                <td colSpan={6} style={{ padding: '4px 8px', textAlign: 'center' }}>
                  <button type="button" onClick={addRow} style={{ background: 'none', border: '1px dashed #d1d5db', padding: '3px 12px', fontSize: 11, color: '#64748b', cursor: 'pointer', borderRadius: 2 }}>
                    + Add Line
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          {/* Item Autocomplete Dropdown */}
          {itemDropdown && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setItemDropdown(null)} />
              <div className="inv-item-dropdown" style={{ position: 'absolute', left: itemDropdown.x, top: itemDropdown.y + 2, minWidth: 320 }}>
                {filteredProducts.length === 0 && !itemSearch && (
                  <div style={{ padding: '8px 10px', color: '#9ca3af', fontSize: 11 }}>No items found</div>
                )}
                {filteredProducts.map(p => (
                  <div key={p.id} className="inv-item-option" onClick={() => selectProduct(itemDropdown.rowId, p)}>
                    <div>
                      <span className="inv-item-option-name">{p.name}</span>
                      {p.sku && <span className="inv-item-option-sku">{p.sku}</span>}
                    </div>
                    <span className="inv-item-option-price">{currencySymbol}{fmt(p.sales_price)}</span>
                  </div>
                ))}
                {itemSearch && (
                  <div
                    className="inv-item-option"
                    style={{ cursor: 'pointer', color: '#2563eb', fontWeight: 600 }}
                    onClick={() => {
                      setNewProduct({ name: itemSearch, sku: '', description: '', sales_price: '', purchase_cost: '', tax_rate: '', product_type: 'NonInventory', category_id: '' });
                      setItemDropdown(null);
                      setItemSearch('');
                      setShowAddProduct(true);
                    }}
                  >
                    <Plus size={14} style={{ marginRight: 6 }} /> Add new product "{itemSearch}"
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* ===== BOTTOM SECTION ===== */}
        <div className="inv-bottom">
          <div className="inv-bottom-left">
            {/* Currency & Exchange Rate */}
            <div className="inv-exchange">
              <label style={{ fontWeight: 600, fontSize: 10, textTransform: 'uppercase' }}>Currency</label>
              <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
                {currencies.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
              </select>
              {docCurrency !== baseCurrency && (
                <>
                  <label style={{ marginLeft: 12, fontWeight: 600, fontSize: 10, textTransform: 'uppercase' }}>Rate</label>
                  <input type="number" step="0.0001" min="0" value={form.exchange_rate} onChange={e => setForm(f => ({ ...f, exchange_rate: e.target.value }))} />
                  <span>{rateLabel(form.currency, effectiveRate, baseCurrency)}</span>
                </>
              )}
            </div>

            {/* Customer Message & Memo */}
            <div className="inv-message-group">
              <label>Customer Message</label>
              <select value={form.customer_message} onChange={e => setForm(f => ({ ...f, customer_message: e.target.value }))}>
                {MESSAGES.map((m, i) => <option key={i} value={m}>{m || '(None)'}</option>)}
              </select>
            </div>
            <div className="inv-message-group">
              <label>Memo</label>
              <input value={form.memo} onChange={e => setForm(f => ({ ...f, memo: e.target.value }))} placeholder="Internal memo..." />
            </div>
          </div>

          <div className="inv-bottom-right">
            {/* Totals */}
            <div className="inv-totals">
              <div className="inv-totals-row">
                <span className="label">Subtotal</span>
                <span className="value">{currencySymbol} {fmt(subtotal)}</span>
              </div>
              <div className="inv-totals-row">
                <span className="label">Discount</span>
                <input className="inv-totals-input" type="number" step="0.01" min="0" value={discount} onChange={e => setDiscount(parseFloat(e.target.value) || 0)} />
              </div>
              <div className="inv-totals-row">
                <span className="label">Tax</span>
                <input className="inv-totals-input" type="number" step="0.01" min="0" value={tax} onChange={e => setTax(parseFloat(e.target.value) || 0)} />
              </div>
              <div className="inv-totals-row">
                <span className="label">Shipping</span>
                <input className="inv-totals-input" type="number" step="0.01" min="0" value={shipping} onChange={e => setShipping(parseFloat(e.target.value) || 0)} />
              </div>
              <div className="inv-totals-row total-row">
                <span className="label">Total</span>
                <span className="value">{currencySymbol} {fmt(total)}</span>
              </div>
              {docCurrency !== baseCurrency && total > 0 && (
                <div className="inv-totals-row inv-fx-total">
                  <span className="label">≈ in {baseCurrency}</span>
                  <span className="value">{fmt(convertAmount(total, effectiveRate))} {baseCurrency}</span>
                </div>
              )}
              <div className="inv-totals-row">
                <span className="label">Payments Applied</span>
                <input className="inv-totals-input" type="number" step="0.01" min="0" value={paymentsApplied} onChange={e => setPaymentsApplied(parseFloat(e.target.value) || 0)} />
              </div>
              <div className="inv-totals-row balance-row">
                <span className="label">Balance Due</span>
                <span className="value">{currencySymbol} {fmt(balanceDue)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ===== ACTION BUTTONS ===== */}
        <div className="inv-actions">
          <button type="button" className="inv-btn inv-btn-danger" onClick={handleClear}>Clear</button>
          <button type="button" className="inv-btn inv-btn-secondary" onClick={() => navigate('/invoices')} disabled={loading}>Cancel</button>
          <button type="button" className="inv-btn inv-btn-secondary" onClick={() => handleSave(false)} disabled={loading}>
            {loading ? 'Saving...' : 'Save & Close'}
          </button>
          <button type="button" className="inv-btn inv-btn-primary" onClick={() => handleSave(true)} disabled={loading}>
            {loading ? 'Saving...' : 'Save & New'}
          </button>
        </div>
      </div>

      {/* Add New Product Modal */}
      {showAddProduct && createPortal(
        <div className="modal-overlay" onClick={() => setShowAddProduct(false)}>
          <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Product</h3>
              <button type="button" className="btn-ghost" onClick={() => setShowAddProduct(false)}>✕</button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!newProduct.name.trim()) { toast.error('Product name is required'); return; }
              setSavingProduct(true);
              try {
                const res = await productsAPI.create({
                  name: newProduct.name.trim(),
                  sku: newProduct.sku.trim() || undefined,
                  description: newProduct.description.trim() || undefined,
                  sales_price: parseFloat(newProduct.sales_price) || 0,
                  purchase_cost: parseFloat(newProduct.purchase_cost) || 0,
                  tax_rate: parseFloat(newProduct.tax_rate) || 0,
                  product_type: newProduct.product_type || 'NonInventory',
                  is_tracked: newProduct.product_type === 'Inventory',
                  category_id: newProduct.category_id ? parseInt(newProduct.category_id) : null,
                });
                const updated = await productsAPI.list({});
                setProducts(updated);
                const created = updated.find(p => p.id === res.id);
                if (created && itemDropdown) {
                  selectProduct(itemDropdown.rowId, created);
                }
                toast.success('Product added');
                setShowAddProduct(false);
              } catch (err) { toast.error(err.message); }
              setSavingProduct(false);
            }}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Name <span className="required">*</span></label>
                    <input className="form-control" autoFocus value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>SKU</label>
                    <input className="form-control" value={newProduct.sku} onChange={e => setNewProduct({ ...newProduct, sku: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Type</label>
                    <select className="form-control" value={newProduct.product_type} onChange={e => setNewProduct({ ...newProduct, product_type: e.target.value })}>
                      <option value="NonInventory">Non-Inventory</option>
                      <option value="Inventory">Inventory</option>
                      <option value="Service">Service</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <select className="form-control" value={newProduct.category_id} onChange={e => setNewProduct({ ...newProduct, category_id: e.target.value })}>
                      <option value="">None</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Description</label>
                    <textarea className="form-control" rows={2} value={newProduct.description} onChange={e => setNewProduct({ ...newProduct, description: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Sales Price</label>
                    <input className="form-control" type="number" step="0.01" min="0" placeholder="0.00" value={newProduct.sales_price} onChange={e => setNewProduct({ ...newProduct, sales_price: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Purchase Price</label>
                    <input className="form-control" type="number" step="0.01" min="0" placeholder="0.00" value={newProduct.purchase_cost} onChange={e => setNewProduct({ ...newProduct, purchase_cost: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Tax %</label>
                    <input className="form-control" type="number" step="0.01" min="0" value={newProduct.tax_rate} onChange={e => setNewProduct({ ...newProduct, tax_rate: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowAddProduct(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={savingProduct}>{savingProduct ? 'Adding...' : 'Add & Select'}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Email Modal */}
      {showEmail && (
        <EmailModal
          show={showEmail}
          onClose={() => setShowEmail(false)}
          type="invoice"
          refId={id}
          number={form.invoice_number}
          recipientEmail={selectedCustomer?.email || ''}
          recipientName={selectedCustomer?.name || ''}
        />
      )}

      {/* Print Preview Modal */}
      {showPrint && (
        <div className="modal-overlay" onClick={() => setShowPrint(false)} style={{ zIndex: 1000 }}>
          <div className="modal" style={{ maxWidth: 800, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Print Invoice</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    const el = printDocRef.current;
                    if (el) printNode(el, `Invoice ${form.invoice_number || ''}`);
                  }}
                >
                  <Printer size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} /> Print
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={async () => { try { await generateInvoicePDF({
                    invoice_number: form.invoice_number,
                    invoice_date: form.invoice_date,
                    due_date: form.due_date,
                    payment_terms: form.terms,
                    status: 'Draft',
                    currency: form.currency,
                    exchange_rate: form.exchange_rate,
                    ...billToDoc,
                    items: rows.filter(r => r.description || r.rate > 0).map(r => ({
                      description: r.description,
                      quantity: r.quantity,
                      unit_price: r.rate,
                      tax_rate: r.tax_rate,
                      tax_amount: (r.rate * r.quantity * (r.tax_rate || 0)) / 100,
                      amount: r.amount,
                    })),
                    subtotal,
                    tax_amount: taxAmount,
                    discount_amount: discount,
                    shipping_amount: shipping,
                    total,
                    amount_paid: paymentsApplied,
                    amount_due: balanceDue,
                    memo: form.customer_message,
                  }, company); } catch(e) { toast.error('PDF failed: ' + e.message); } }}
                >
                  <Download size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} /> PDF
                </button>
                <button type="button" className="btn-ghost" onClick={() => setShowPrint(false)}>✕</button>
              </div>
            </div>
            <div className="modal-body" style={{ padding: 0 }}>
              <div ref={printDocRef} style={{ padding: 20 }}>
                <InvoiceDocument data={{
                  invoice_number: form.invoice_number,
                  invoice_date: form.invoice_date,
                  due_date: form.due_date,
                  status: form.status,
                  payment_terms: form.terms,
                  currency: form.currency,
                  exchange_rate: form.exchange_rate,
                  ...billToDoc,
                  subtotal,
                  tax_amount: taxAmount,
                  total,
                  amount_paid: 0,
                  amount_due: total - paymentsApplied,
                  memo: form.customer_message,
                  items: rows.filter(r => r.description || r.rate > 0).map(r => ({
                    description: r.description,
                    quantity: r.quantity,
                    unit_price: r.rate,
                    tax_rate: r.tax_rate,
                    tax_amount: (r.rate * r.quantity * (r.tax_rate || 0)) / 100,
                    amount: r.amount,
                  })),
                }} docType="invoice" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
